// Client HTTP unique. L'authentification se fait par en-tête Bearer (pas de
// cookie, donc pas de withCredentials). Toutes les erreurs de l'API ont la
// forme { message, code, errors? } : elles sont normalisées en ApiError.

// URL de l'API : VUE_APP_API_URL si elle est définie, sinon la même machine que le front, port 8000.
// Ouvert via http://192.168.100.26:8080, le front appelle donc http://192.168.100.26:8000 (et non
// « localhost », qui désignerait l'appareil du navigateur, pas celui de l'API).
const DEFAULT_API_URL = `${window.location.protocol}//${window.location.hostname}:8000/api/v1`
export const BASE_URL = (process.env.VUE_APP_API_URL || DEFAULT_API_URL).replace(/\/$/, '')

const TOKEN_KEY = 'gesrecette.token'

export const tokenStore = {
  get() {
    try {
      return localStorage.getItem(TOKEN_KEY)
    } catch (e) {
      return null
    }
  },
  set(token) {
    try {
      localStorage.setItem(TOKEN_KEY, token)
    } catch (e) {
      /* stockage indisponible : la session ne survivra pas au rechargement */
    }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY)
    } catch (e) {
      /* rien à faire */
    }
  }
}

export class ApiError extends Error {
  constructor({ status, code, message, errors, retryAfter }) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code || null
    this.errors = errors || null
    this.retryAfter = retryAfter || null
  }

  get isValidation() {
    return this.status === 422 && !!this.errors
  }
}

let token = tokenStore.get()
let unauthorizedHandler = () => {}

export function setToken(value) {
  token = value
  if (value) tokenStore.set(value)
  else tokenStore.clear()
}

export function getToken() {
  return token
}

export function onUnauthorized(handler) {
  unauthorizedHandler = handler
}

/** ?filter[status]=a,b — un tableau est joint par des virgules (« ou »). */
export function buildQuery(params) {
  const search = new URLSearchParams()
  const add = (key, value) => {
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value)) {
      if (value.length) search.append(key, value.join(','))
    } else {
      search.append(key, String(value))
    }
  }
  for (const [key, value] of Object.entries(params || {})) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [sub, subValue] of Object.entries(value)) add(`${key}[${sub}]`, subValue)
    } else {
      add(key, value)
    }
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

async function toApiError(response) {
  let body = null
  try {
    body = await response.json()
  } catch (e) {
    body = null
  }
  const retryAfter = Number(response.headers.get('Retry-After')) || null
  let message = body?.message
  if (!message) {
    message = {
      401: 'Votre session a expiré. Veuillez vous reconnecter.',
      403: "Vous n'avez pas accès à cette ressource.",
      404: 'Ressource introuvable.',
      429: 'Trop de tentatives. Réessayez dans un instant.',
      500: 'Erreur du serveur. Réessayez plus tard.'
    }[response.status] || `Erreur inattendue (${response.status}).`
  }
  if (response.status === 429 && retryAfter) {
    message = `${message} (réessayez dans ${retryAfter} s)`
  }
  return new ApiError({
    status: response.status,
    code: body?.code,
    message,
    errors: body?.errors,
    retryAfter
  })
}

async function send(method, path, { params, body, form, signal, auth = true } = {}) {
  const headers = { Accept: 'application/json', 'Accept-Language': 'fr' }
  if (auth && token) headers.Authorization = `Bearer ${token}`
  let payload
  if (form) {
    payload = form // multipart : le navigateur fixe le Content-Type et la frontière
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  let response
  try {
    response = await fetch(`${BASE_URL}${path}${buildQuery(params)}`, { method, headers, body: payload, signal })
  } catch (e) {
    if (e.name === 'AbortError') throw e
    throw new ApiError({ status: 0, code: 'network_error', message: "Impossible de joindre le serveur. Vérifiez votre connexion." })
  }

  if (!response.ok) {
    const error = await toApiError(response)
    // Seul « unauthenticated » signifie « reconnectez-vous » : une 401 de
    // connexion (invalid_credentials) doit rester affichée dans le formulaire.
    if (error.code === 'unauthenticated' || error.code === 'account_disabled') {
      unauthorizedHandler(error)
    }
    throw error
  }
  return response
}

async function json(method, path, options) {
  const response = await send(method, path, options)
  if (response.status === 204) return null
  const text = await response.text()
  return text ? JSON.parse(text) : null
}

export const http = {
  get: (path, options) => json('GET', path, options),
  post: (path, body, options) => json('POST', path, { ...options, body }),
  put: (path, body, options) => json('PUT', path, { ...options, body }),
  delete: (path, options) => json('DELETE', path, options),
  upload: (path, form, options) => json('POST', path, { ...options, form }),
  /** Fichier protégé par le token (justificatif, rapport) : récupéré en blob. */
  async blob(path, options) {
    const response = await send('GET', path, options)
    return { blob: await response.blob(), type: response.headers.get('Content-Type') }
  }
}

/** Déclenche l'enregistrement d'un blob sous le nom donné. */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}
